import { useQuery } from "@tanstack/react-query";
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell, ReferenceDot } from "recharts";
import { TrendingUp, TrendingDown, Minus, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { fmtNum } from "@/lib/format";

const TT = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="rounded-md border border-slate-200 bg-white p-3 text-xs shadow-xl">
      <div className="font-semibold mb-1">{label} · {d.date}</div>
      <div className="font-mono space-y-0.5 text-slate-700">
        <div>Headcount {d.headcount} (DL {d.dl} · IDL {d.idl} · absent {d.absent})</div>
        <div>OT {d.ot_hours} hrs · {d.ot_man_hours} man-hrs</div>
        <div>Output {fmtNum(d.actual)} · MVA ₹{fmtNum(Math.round(d.mva))}</div>
        <div>MVA/head {d.mva_per_mp == null ? "—" : `₹${fmtNum(d.mva_per_mp)}`} · units/head {d.units_per_mp ?? "—"}</div>
        {d.ot_flag && <div className="mt-1 rounded bg-rose-50 px-2 py-1 text-rose-800 font-semibold">⚑ OT man-hrs {d.ot_flag_detail.ot_delta > 0 ? "+" : ""}{d.ot_flag_detail.ot_delta} vs {d.ot_flag_detail.prev_date.slice(5)} but units/head {d.ot_flag_detail.upmp_delta_pct}%</div>}
        {!d.entries && <div className="text-slate-400">No headcount entered</div>}
      </div>
    </div>
  );
};

function Stat({ label, value, delta, testId }) {
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 p-3" data-testid={testId}>
      <div className="pp-label">{label}</div>
      <div className="font-mono text-xl font-bold flex items-center gap-2">{value}
        {delta != null && <span className={`text-xs flex items-center gap-0.5 ${delta > 0 ? "text-emerald-600" : delta < 0 ? "text-rose-600" : "text-slate-400"}`}>{delta > 0 ? <TrendingUp size={14} /> : delta < 0 ? <TrendingDown size={14} /> : <Minus size={14} />}{delta > 0 ? "+" : ""}{delta.toFixed(1)}%</span>}
      </div>
    </div>
  );
}

export function ManpowerTrend({ plantId, reportingDate }) {
  const q = useQuery({
    queryKey: ["manpower-trend", plantId, reportingDate],
    queryFn: async () => (await api.get("/manpower/trend", { params: { end_date: reportingDate, days: 7, plant_id: plantId || undefined } })).data,
    enabled: !!plantId,
  });
  const t = q.data; const rows = t?.series || [];
  const filled = rows.filter((r) => r.headcount);
  const last = filled[filled.length - 1], prev = filled[filled.length - 2];
  const delta = (k) => (last && prev && prev[k] ? ((last[k] - prev[k]) * 100) / prev[k] : null);
  return (
    <div className="pp-card p-5 rise rise-3" data-testid="manpower-trend-card">
      <div className="mb-3">
        <div className="pp-label">7-day manpower trend</div>
        <div className="text-sm text-slate-500">Headcount (bars) · OT hours (blue) · MVA per head (purple) — red bar / ⚑ = OT man-hours rose but output per head fell vs the previous day</div>
      </div>
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 mb-4">
        <Stat testId="mp-trend-headcount" label="Avg headcount" value={t ? fmtNum(Math.round(t.avg.headcount)) : "—"} delta={delta("headcount")} />
        <Stat testId="mp-trend-ot" label="Avg OT hrs/day" value={t ? t.avg.ot_hours : "—"} delta={delta("ot_hours")} />
        <Stat testId="mp-trend-mva" label="Avg MVA / head" value={t ? `₹${fmtNum(t.avg.mva_per_mp)}` : "—"} delta={delta("mva_per_mp")} />
        <Stat testId="mp-trend-units" label="Avg units / head" value={t ? t.avg.units_per_mp : "—"} delta={delta("units_per_mp")} />
      </div>
      <div className="h-64 w-full min-h-[256px]" data-testid="manpower-trend-chart">
        <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 600, height: 240 }} minHeight={200}>
          <ComposedChart data={rows} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid stroke="#E2E8F0" vertical={false} />
            <XAxis dataKey="label" tick={{ fill: "#64748B", fontSize: 12, fontFamily: "IBM Plex Mono" }} />
            <YAxis yAxisId="l" tick={{ fill: "#64748B", fontSize: 11, fontFamily: "IBM Plex Mono" }} />
            <YAxis yAxisId="r" orientation="right" tick={{ fill: "#64748B", fontSize: 11, fontFamily: "IBM Plex Mono" }} />
            <Tooltip content={<TT />} cursor={{ fill: "rgba(100,116,139,0.08)" }} />
            <Legend wrapperStyle={{ fontSize: 12, color: "#64748B" }} />
            <Bar yAxisId="l" dataKey="dl" name="DL present" stackId="hc" fill="#3B82F6" maxBarSize={48}>{rows.map((r) => <Cell key={r.date} fill={r.ot_flag ? "#F43F5E" : "#3B82F6"} />)}</Bar>
            <Bar yAxisId="l" dataKey="idl" name="IDL present" stackId="hc" fill="#93C5FD" radius={[4, 4, 0, 0]} maxBarSize={48} />
            <Line yAxisId="l" type="monotone" dataKey="ot_hours" name="OT hours" stroke="#0EA5E9" strokeWidth={2} dot={{ r: 3 }} />
            <Line yAxisId="r" type="monotone" dataKey="mva_per_mp" name="MVA / head (₹)" stroke="#7C3AED" strokeWidth={2} dot={{ r: 4, fill: "#7C3AED" }} connectNulls />
            {rows.filter((r) => r.ot_flag).map((r) => <ReferenceDot key={r.date} yAxisId="l" x={r.label} y={r.headcount} r={7} fill="#F43F5E" stroke="#fff" strokeWidth={2} label={{ value: "⚑", position: "top", fill: "#BE123C", fontSize: 12 }} />)}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      {t?.ot_flags?.length > 0 && <div className="mt-3 rounded-md border border-rose-200 bg-rose-50 p-3 text-xs" data-testid="ot-vs-output-flags">
        <div className="flex items-center gap-1.5 font-semibold text-rose-800"><AlertTriangle size={14} /> OT vs output · {t.ot_flags.length} day(s) where OT man-hours rose but output per head fell</div>
        <ul className="mt-1 space-y-0.5 text-rose-900">{t.ot_flags.map((f) => <li key={f.date} className="font-mono" data-testid={`ot-flag-${f.date}`}>{f.label} ({f.date}): OT man-hrs {f.ot_flag_detail.ot_delta > 0 ? "+" : ""}{f.ot_flag_detail.ot_delta}{f.ot_flag_detail.ot_delta_pct != null ? ` (${f.ot_flag_detail.ot_delta_pct > 0 ? "+" : ""}${f.ot_flag_detail.ot_delta_pct}%)` : ""} → {f.ot_man_hours} · units/head {f.ot_flag_detail.upmp_prev} → {f.units_per_mp} ({f.ot_flag_detail.upmp_delta_pct}%) vs {f.ot_flag_detail.prev_date.slice(5)}</li>)}</ul>
        <div className="mt-1 text-rose-700">Extra OT did not convert into output — check line balancing, material availability and downtime on these days.</div>
      </div>}
      {t && !t.days_with_data && <div className="mt-2 text-xs text-slate-500">No headcount entries in this window yet — save daily manpower above to build the trend.</div>}
    </div>
  );
}
